import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Flex,
  Link,
  Portal,
} from "@chakra-ui/react";
import React from "react";
import { DeleteFormTemplateButton } from "src/components/Buttons/DeleteFormTemplateButton";
import { useAlertPortal } from "src/components/Layout/BaseLayout";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabsWithHash,
} from "src/components/Layout/TabsWithHash";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { ContentTab } from "src/scenes/orgAdmin/formTemplates/ContentTab/ContentTab";
import { RulesTab } from "src/scenes/orgAdmin/formTemplates/RulesTab";
import { SettingsTab } from "src/scenes/orgAdmin/formTemplates/SettingsTab";
import { StatusesTab } from "src/scenes/orgAdmin/formTemplates/StatusesTab";
import { FORM_TEMPLATE_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { AutomaticMessagesTab } from "./AutomaticMessagesTab";
import { useFormTemplateContext } from "./formTemplateContext";
import { useFormBuilderV1Feature } from "./hooks/useFormBuilderV1Feature";

type EditFormTemplateProps = {
  formTemplate: GQL.EditFormTemplateFragment;
};
export const Edit: React.FC<EditFormTemplateProps> = (props) => {
  const { formTemplate } = props;
  const { actionRef } = useFormTemplateContext();
  const organization = useOrganization();

  const { isFormBuilderReadOnly, isFormBuilderFullEdit } =
    useFormBuilderV1Feature();

  const canEditFormTemplateRules = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  return (
    <>
      <Portal containerRef={actionRef}>
        <DeleteFormTemplateButton
          id={formTemplate.id}
          formTemplateName={formTemplate.name}
        />
      </Portal>
      <PublishedAlert mode={formTemplate.mode} />
      <Flex direction="column" grow="1" minHeight="100%">
        <TabsWithHash variant="admin" isLazy>
          <TabList>
            {(isFormBuilderFullEdit || isFormBuilderReadOnly) && (
              <Tab hashKey={FORM_TEMPLATE_TAB_HASH.CONTENT}>Content</Tab>
            )}
            <Tab hashKey={FORM_TEMPLATE_TAB_HASH.STATUSES}>Statuses</Tab>
            <Tab hashKey={FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES}>
              Automatic messages
            </Tab>
            <Tab hashKey={FORM_TEMPLATE_TAB_HASH.SETTINGS}>Settings</Tab>
            {canEditFormTemplateRules && (
              <Tab hashKey={FORM_TEMPLATE_TAB_HASH.RULES}>Rules</Tab>
            )}
          </TabList>
          <TabPanels>
            {(isFormBuilderFullEdit || isFormBuilderReadOnly) && (
              <TabPanel>
                <ContentTab />
              </TabPanel>
            )}
            <TabPanel>
              <StatusesTab />
            </TabPanel>
            <TabPanel>
              <AutomaticMessagesTab />
            </TabPanel>
            <TabPanel>
              <SettingsTab />
            </TabPanel>
            {canEditFormTemplateRules && (
              <TabPanel>
                <RulesTab organization={organization} />
              </TabPanel>
            )}
          </TabPanels>
        </TabsWithHash>
      </Flex>
    </>
  );
};

const PublishedAlert = ({ mode }: { mode: GQL.form_template_mode_enum }) => {
  const flags = useFlags([
    "form-builder-draft-mode",
    "form-template-content-export-import",
  ]);

  const { AlertPortal } = useAlertPortal();

  return flags["form-builder-draft-mode"].enabled &&
    mode === GQL.form_template_mode_enum.Published ? (
    <AlertPortal>
      <Alert status="info" variant="solid">
        <AlertIcon />
        <AlertTitle>This form has been published.</AlertTitle>
        <AlertDescription>
          To preserve data integrity, there are fewer things you can do with it.{" "}
          <Link
            textDecoration="underline"
            href="https://avelahelp.zendesk.com/hc/en-us/articles/37978319952013-Live-Edit"
            target="_blank"
          >
            Learn more
          </Link>
        </AlertDescription>
      </Alert>
    </AlertPortal>
  ) : null;
};
